/* @flow */

import type { Match } from "react-router";

import React, { useEffect, useRef, useContext } from "react";
import cn from "classnames";
import { disableBodyScroll, enableBodyScroll } from "body-scroll-lock";
import { Link, NavLink } from "react-router-dom";
import { useData } from "crustate/react";
import { useTranslate } from "@awardit/react-use-translate";
import { StoreInfoContext } from "entrypoint/shared";
import { QuoteData } from "data";
import useFormat from "helpers/use-format";
import { useUi } from "helpers/ui";
import { MODE } from "state/view-mode";
import Button from "components/Button";

import SearchIcon from "icons/search.svg";

import styles from "./styles.scss";

const TopMenu = (): React$Node => {
  const t = useTranslate();
  const { setModeAndOpenMenu: setMode, viewMode } = useUi();
  const { categories } = useContext(StoreInfoContext);

  const navLinkIsActive = (match: Match) => {
    if (!match) {
      return false;
    }

    if (viewMode !== MODE.NORMAL) {
      return false;
    }

    return true;
  };

  return (
    <div className={styles.topMenu}>

      {categories.map(category => (
        <NavLink
          key={category.name}
          to={category.url}
          activeClassName={styles.active}
          className={cn(
            styles.item,
            styles.item__allproducts
          )}
          isActive={navLinkIsActive}
        >
          <span>{category.name}</span>
        </NavLink>
      ))}

      <Link className={styles.item} to={t("CUSTOMER_SERVICE.LINK")}>
        <span>{t("CUSTOMER_SERVICE.TEXT")}</span>
      </Link>

      <Button
        aria-expanded={viewMode === MODE.SEARCH ?? "true"}
        aria-controls="searchForm"
        className={styles.item}
        onClick={() => setMode(MODE.SEARCH)}
      >
        <span>
          <SearchIcon aria-label={t("ICONS.SEARCH")} />
          <span>{t("HEADER.SEARCH")}</span>
        </span>
      </Button>

      <CartButton />
    </div>
  );
};

const CartButton = (): React$Node => {
  const t = useTranslate();
  const { formatPrice } = useFormat();
  const quote = useData(QuoteData).data || null;
  const cartCount = quote?.items.reduce((a, { qty }) => a + qty, 0) || 0;

  return (
    <Link
      className={styles.item}
      aria-label={t("HEADER.CART")}
      to={t("URL.CHECKOUT_CART")}
    >
      <span>{t("HEADER.CART")}</span>
      <span>
        <span className={styles.grandTotal}>
          {formatPrice(quote?.grandTotal.incVat || 0)}
        </span>
        <span className={styles.cartCount}>&nbsp;({cartCount})</span>
      </span>
    </Link>
  );
};

const HamburgerMenu = ({ children }: { children: React$Node }): React$Node => {
  const {
    hamburgerOpen,
    isHamburgerOpening,
    isHamburgerClosing,
    subNavOpen,
  } = useUi();

  const ref = useRef();

  useEffect(() => {
    const element = ref.current;

    if (hamburgerOpen && !subNavOpen) {
      if (element) {
        // Select the first focusable element inside the Menu
        const focusable = element.querySelector(
          "button, [href], input, select, textarea, [tabindex]:not([tabindex='-1'])"
        );

        if (focusable instanceof HTMLElement) {
          focusable.focus();
        }
      }

      disableBodyScroll(element);
    }
    else {
      enableBodyScroll(element);
    }

    return () => {
      if (element) {
        enableBodyScroll(element);
      }
    };
  }, [subNavOpen, hamburgerOpen]);

  return (
    <div
      id="hamburgerMenu"
      className={cn(
        styles.block,
        { [styles.open]: hamburgerOpen },
        { [styles.isOpening]: isHamburgerOpening },
        { [styles.isClosing]: isHamburgerClosing },
        { [styles.subNavOpen]: subNavOpen }
      )}
    >
      <div ref={ref} className={styles.hamburgerMenu}>
        <TopMenu />
      </div>

      {children}
    </div>
  );
};

export default HamburgerMenu;
